import { useRef, useState } from 'react';
import { Upload } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import type { RcFile, UploadChangeParam, UploadFile } from 'antd/lib/upload';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import Notification from 'components/ui/base/notification/Notification';
import { useInvoiceContext } from '../../InvoiceContextProvider';
import useRequest from 'hooks/useRequest';
import useFormUtils from 'hooks/useFormUtils';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { confirmUploadFile, documentsPreUpload, downloadFile } from 'store/documents/client';
import { deleteDeclarationDocument } from 'store/declarations/client';
import {
    attachDocumentToDeclaration,
    DeclarationDocumentAttachPayload,
    validateDeclarationFile,
} from 'store/file-upload/utils';
import { uploadFile } from 'utils/uploadFile';
import Document from './Document';
import { InvoiceFileState, InvoiceFileTypes } from 'store/invoices/types';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import { isValidInvoiceFile } from './utils';

type CommercialInvoiceFile = {
    fileId?: string;
    declarationFileId?: string;
    fileName: string;
    fileReference: string;
};

type Props = {
    fileId?: string;
    fileName?: string;
    fileReference?: string;
    state?: InvoiceFileState;
    onPreview?: () => void;
    onPreviewWithUrl?: (url: string) => void;
    onUpload: (fileReference: string) => void;
    previewLoading?: boolean;
};

const CommercialInvoiceDocument = ({
    fileId,
    fileName,
    fileReference,
    state,
    onPreview,
    onPreviewWithUrl,
    onUpload,
    previewLoading,
}: Props) => {
    const { invoice } = useInvoiceContext();
    const uploadRef = useRef<any>(null);

    const [uploadedFile, setUploadedFile] = useState<CommercialInvoiceFile | undefined>(undefined);
    const [isDeleting, setIsDeleting] = useState(false);

    const apiFile =
        fileReference && isValidInvoiceFile({ fileId, fileName, state })
            ? { fileId, fileName, fileReference }
            : undefined;
    const commercialInvoiceFile = apiFile ?? uploadedFile;
    const declarationId = invoice?.declarationId;
    const customerId = invoice?.customer.id;

    const { data: preUploadData, doRequest: preUpload } = useRequest(documentsPreUpload);
    const { isAis, isAes, isNcts, isCds } = useFormUtils(declarationId);
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const handleUploadChange = async ({ file }: UploadChangeParam<UploadFile>) => {
        if (!preUploadData?.preSignedUrl || !customerId || !declarationId) {
            Notification({
                type: 'error',
                messageTitle: 'Upload error',
                description: 'Missing upload metadata. Please try again.',
                placement: 'top',
            });
            hideGlobalOverlay();
            return;
        }

        try {
            await uploadFile(file as unknown as File, preUploadData.preSignedUrl);

            const s3id = preUploadData.preSignedUrl.match(/\/([^/?]+)\?/)?.[1];
            if (!s3id) throw new Error('Missing file identifier');

            const { payload: cmsFile } = await confirmUploadFile({
                s3id,
                fileName: file.name,
                fileSize: file.size,
                customerId,
                declarationId,
            });

            const attachPayload: DeclarationDocumentAttachPayload = {
                fileId: cmsFile.id,
                fileReference: cmsFile.link,
                fileName: cmsFile.filename,
                fileInfo: { documentType: cmsFile.contentType },
                invoice: true,
                processInvoiceWithTee: false,
            };

            const declarationDocument = await attachDocumentToDeclaration(attachPayload, declarationId, {
                isAis,
                isAes,
                isNcts,
                isCds,
            });

            setUploadedFile({
                fileName: cmsFile.filename,
                fileReference: cmsFile.link,
                fileId: cmsFile.id,
                declarationFileId: declarationDocument?.id,
            });

            Notification({
                type: 'success',
                messageTitle: 'Commercial Invoice Uploaded',
                description: `${file.name} uploaded successfully.`,
                placement: 'top',
            });

            onUpload(cmsFile.link);
        } catch (err) {
            const errorMessage = getErrorMessage(err) || DEFAULT_INTERNAL_ERROR_MESSAGE;
            Notification({
                type: 'error',
                messageTitle: 'Upload Failed',
                description: errorMessage,
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    const [isPreviewFetching, setIsPreviewFetching] = useState(false);

    const handlePreviewClick = async () => {
        if (!uploadedFile?.fileId || !onPreviewWithUrl) return;

        try {
            setIsPreviewFetching(true);
            const { preSignedUrl } = await downloadFile(uploadedFile.fileId);
            onPreviewWithUrl(preSignedUrl);
        } catch (err) {
            Notification({
                type: 'error',
                messageTitle: 'Preview Failed',
                description: 'Document preview is not ready yet. Please try again shortly.',
                placement: 'top',
            });
        } finally {
            setIsPreviewFetching(false);
        }
    };

    const handleDelete = async (declarationFileId: string | undefined) => {
        if (!declarationFileId || !declarationId) {
            Notification({
                type: 'error',
                messageTitle: 'Deletion Failed',
                description: DEFAULT_INTERNAL_ERROR_MESSAGE,
                placement: 'top',
            });
            return;
        }

        setIsDeleting(true);
        try {
            await deleteDeclarationDocument(declarationId, declarationFileId);
            setUploadedFile(undefined);

            Notification({
                type: 'success',
                messageTitle: 'Deletion Successful',
                description: 'Successfully removed commercial invoice.',
                placement: 'top',
            });
        } catch (err) {
            const errorMessage = getErrorMessage(err) || DEFAULT_INTERNAL_ERROR_MESSAGE;
            Notification({
                type: 'error',
                messageTitle: 'Deletion Failed',
                description: errorMessage,
                placement: 'top',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Document
            fileName={commercialInvoiceFile?.fileName ?? 'Commercial Invoice Missing'}
            documentType={InvoiceFileTypes.COMMERCIAL_INVOICE}
            isHighlighted={!commercialInvoiceFile}
            onPreview={
                commercialInvoiceFile ? (uploadedFile && onPreviewWithUrl ? handlePreviewClick : onPreview) : undefined
            }
            previewLoading={previewLoading || isPreviewFetching}
            actions={
                apiFile ? undefined : uploadedFile ? (
                    <IconButton
                        tooltipText="Delete"
                        icon={<DeleteOutlined />}
                        loading={isDeleting}
                        onClick={() => handleDelete(uploadedFile?.declarationFileId)}
                    />
                ) : (
                    <div style={{ display: 'inline-flex' }}>
                        <IconButton
                            onClick={() => uploadRef.current?.upload.uploader.fileInput.click()}
                            tooltipText="Upload"
                            icon={<UploadOutlined />}
                        />
                        <Upload
                            ref={uploadRef}
                            style={{ display: 'none' }}
                            showUploadList={false}
                            maxCount={1}
                            beforeUpload={(file: RcFile) => {
                                return validateDeclarationFile(
                                    file,
                                    customerId,
                                    declarationId,
                                    preUpload,
                                    isAes,
                                    showGlobalOverlay
                                );
                            }}
                            onChange={handleUploadChange}
                        />
                    </div>
                )
            }
        />
    );
};

export default CommercialInvoiceDocument;
